import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../infrastructure/api/api.service';
import {
  DATACIZE_STREAM_REQUEST_MODE,
  DATACIZE_STREAM_URIS,
  IDatacizeStream,
  IDatacizePoint,
  TDatacizeStream,
  TDatacizeStreamRequest,
  TDatacizeStreamUpdateRequest,
  DatacizePoint,
  TDatacizeStreamUpdateResponse,
  DATACIZE_STREAMS,
  CounterpartiesCompact,
} from '@aksia/models';
import { concatMap, map, Observable } from 'rxjs';
import {
  ENVIRONMENT_TOKEN,
  IErrorResult,
  PropExtend,
  UTILS,
} from '@aksia/infrastructure';
import { EntityTypeEnum } from '@aksia/enums';

@Injectable({
  providedIn: 'root',
})
export class DatacizeStreamService {
  private readonly apiSvc = inject(ApiService);
  private readonly environment = inject(ENVIRONMENT_TOKEN);

  public updateDatacize = (
    streamsRequest: TDatacizeStreamUpdateRequest,
  ): Observable<TDatacizeStreamUpdateResponse | IErrorResult> => {
    const uri = DATACIZE_STREAM_URIS.URI(
      streamsRequest.entityId!,
      streamsRequest.entityTypeId!,
      DATACIZE_STREAM_REQUEST_MODE.UPDATE,
    );
    const body = {
      dataAsOfDate: streamsRequest.dataAsOfDate,
      dataSource: streamsRequest.dataSource,
      [streamsRequest.entityTypeId === EntityTypeEnum.ManagementCompany
        ? 'managementCompanyId'
        : 'fundId']: streamsRequest.entityId,
      stream: {} as any,
    };
    streamsRequest.stream.forEach((stream: IDatacizeStream) => {
      const datapoints = this.removeEmptyDatapoints(stream.datapoints);
      // If typeIds is null or undefined, Create an array with a single null value for the service to save it.
      // NOTE: only a point that holds a figure is padded. A removed counterparty
      // has every field cleared, and padded first it held `[null]`, so the
      // empty-point filter kept it and the row came back empty after a reload.
      if (stream.streamType === DATACIZE_STREAMS.COUNTERPARTIES) {
        datapoints.forEach((point) => {
          if (!(point as CounterpartiesCompact)?.typeIds) {
            (point as any).typeIds = [null];
          }
        });
      }
      body.stream[stream.streamType!] = datapoints;
    });

    return this.apiSvc.post(uri, body);
  };

  public getDatacize = (streamsRequest: TDatacizeStreamRequest) => {
    const uri = DATACIZE_STREAM_URIS.URI(
      streamsRequest.entityId,
      streamsRequest.entityTypeId,
      DATACIZE_STREAM_REQUEST_MODE.HISTORICAL,
    );
    return this.apiSvc.get(uri).pipe(
      map((response: PropExtend<unknown>) => {
        let streamsDTO: Array<IDatacizeStream> = [];
        for (const key in response) {
          streamsDTO.push({
            entityId: streamsRequest.entityId,
            entityTypeId: streamsRequest.entityTypeId,
            streamType: key as TDatacizeStream,
            datapoints: response[key] as Array<IDatacizePoint>,
          });
        }
        return streamsDTO;
      }),
    );
  };

  public uploadDatacize = (streamsRequest: TDatacizeStreamUpdateRequest) => {
    streamsRequest.stream.forEach((stream: IDatacizeStream) => {
      stream.datapoints = this.removeEmptyDatapoints(stream.datapoints);
    });
    return this.getDatacize({
      entityId: streamsRequest.entityId!,
      entityTypeId: streamsRequest.entityTypeId!,
    }).pipe(
      concatMap((existingStreams: Array<IDatacizeStream>) => {
        // Merge existing datapoints with new ones, avoiding duplicates
        if (existingStreams?.flatMap((s) => s.datapoints).length === 0) {
          return this.updateDatacize(streamsRequest);
        }

        streamsRequest.stream.forEach((stream: IDatacizeStream) => {
          stream.datapoints = [
            ...(stream.datapoints ?? []),
            ...(existingStreams
              ?.find((s) => s.streamType === stream.streamType)
              ?.datapoints?.filter(
                (point) =>
                  !stream.datapoints?.some((newpoint) =>
                    UTILS.DATE.isSameDate(point.asOfDate, newpoint.asOfDate),
                  ),
              ) ?? []),
          ];
        });
        return this.updateDatacize(streamsRequest);
      }),
    );
  };

  private removeEmptyDatapoints(
    datapoints: Array<IDatacizePoint> | undefined,
    softRemove = false,
  ): Array<IDatacizePoint> {
    if (!datapoints || datapoints.length === 0) {
      return [];
    }
    let dp = new DatacizePoint();

    const atLeastOneKeyHasValue = (point: IDatacizePoint) =>
      Object.keys(point).some(
        (key) =>
          !UTILS.OBJECT.isNil(point[key as keyof IDatacizePoint]) &&
          !Object.hasOwn(dp, key),
      );

    let result = softRemove
      ? datapoints?.map((point) => ({
          ...point,
          isDeleted: !atLeastOneKeyHasValue(point),
        }))
      : datapoints?.filter((point) => {
          return atLeastOneKeyHasValue(point);
        });

    return result;
  }
}
